/*
<summary>
   Operator Assign Task "Tool Scan" button and popup (Batch Mode locks it once Started/Paused;
   Single Mode allows scanning after Start too). Each scan is validated server-side via
   GetValidateBomToolStatusByTool. 
</summary>
<param>isProcessStarted: disables Tool Scan when true (Batch Mode only).</param>
<param>selectedTrayId, taskId: Single Mode only - triggers immediate Save reporting.</param>
<param>processName: current process</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { XCircleFill } from 'react-bootstrap-icons';
import { useStore } from '../../../contexts/store-provider';
import { formatMessage } from '../../../translations/format-message';
import {
  getTenantIdFromLocalStorage,
  getToolBOMFactoryIdFromLocalStorage,
} from '../../../helpers/localstorage-helper';

interface IProps {
  isProcessStarted?: boolean;
  selectedTrayId?: string | null;
  taskId?: number | string;
  processName?: string | null;
  hasTray?: boolean;
}

const ToolScanControl = ({ isProcessStarted = false, selectedTrayId, taskId, processName, hasTray = true }: IProps) => {
  const { toolBOMStore } = useStore();
  const {
    scannedToolNumbers,
    scannedToolMainUnitNumbers,
    sentScannedToolNumbers,
    addScannedTool,
    removeScannedTool,
    ReportIncrementalToolStart,
    setToolScanPopupOpen,
    selectedToolModel,
    GetToolBomToolManagementListService,
  } = toolBOMStore;

  // Single Mode only - once a tool has been saved to the DB (ReportIncrementalToolStart), it
  // can no longer be pulled back out of the popup, just viewed.
  const isSingleMode = !!(selectedTrayId && taskId);

  const [showPopup, setShowPopup] = useState(false);
  const [scanValue, setScanValue] = useState('');
  const [scanError, setScanError] = useState<{ key?: string; message?: string; toolNo: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Same rapid-keystroke buffer pattern used for tray scanning in the operator screen (see
  // OperatorAssignBatch's window keydown handler) - lets the operator scan a tool even if the
  // popup's input isn't focused, instead of the scan being lost into whatever else has focus.
  const [scanBuffer, setScanBuffer] = useState('');
  const [lastKeyTime, setLastKeyTime] = useState(Date.now());

  // Tools can only be scanned once a tray is loaded
  const canScan = !isProcessStarted && hasTray;

  useEffect(() => {
    if (showPopup) {
      // Re-focus after every scan so a barcode scanner (keyboard-wedge input) can keep firing
      // Enter-terminated reads without the operator needing to click back into the field.
      inputRef.current?.focus();
    }
  }, [showPopup, scannedToolNumbers.length]);

  useEffect(() => {
    setToolScanPopupOpen(showPopup);
    return () => setToolScanPopupOpen(false);
  }, [showPopup, setToolScanPopupOpen]);

  useEffect(() => {
    if (isProcessStarted || !hasTray) {
      setShowPopup(false);
    }
  }, [isProcessStarted, hasTray]);

  const handleScanSubmit = (rawValue?: string) => {
    const value = (rawValue ?? scanValue).trim();
    if (!value || isSubmitting) return;

    setIsSubmitting(true);
    addScannedTool(
      value,
      getToolBOMFactoryIdFromLocalStorage() || '',
      Number(getTenantIdFromLocalStorage()),
      processName
    )
      .then((result) => {
        if (result.success) {
          setScanValue('');
          setScanBuffer('');
          setScanError(null);
        } else if (result.errorKey || result.errorMessage) {
          setScanError({ key: result.errorKey, message: result.errorMessage, toolNo: value });
        }
      })
      .finally(() => setIsSubmitting(false));
  };

  useEffect(() => {
    if (!showPopup) return;

    const handleWindowKeyDown = (e: KeyboardEvent) => {
      // The visible input already fully handles typing/editing/Enter-submit while it has
      // focus (including Backspace) - this global listener only steps in when a scan comes in
      // while focus is elsewhere, mirroring how tray scanning works from anywhere on screen.
      if (document.activeElement === inputRef.current) return;
      if (!e.key) return;

      if (
        e.key === 'Shift' ||
        e.key === 'Control' ||
        e.key === 'Alt' ||
        e.key === 'CapsLock' ||
        (e.key.length > 1 && e.key !== 'Enter')
      ) {
        return;
      }

      const currentTime = Date.now();
      const timeDiff = currentTime - lastKeyTime;

      if (e.key === 'Enter') {
        if (timeDiff > 100) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        const cleanedScan = scanBuffer.trim();
        handleScanSubmit(cleanedScan);
        setScanBuffer('');
        return;
      }

      if (timeDiff > 100) {
        setScanBuffer(e.key);
      } else {
        setScanBuffer((prev) => prev + e.key);
      }

      setLastKeyTime(currentTime);
    };

    window.addEventListener('keydown', handleWindowKeyDown);
    return () => window.removeEventListener('keydown', handleWindowKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopup, scanBuffer, lastKeyTime]);

  const closePopup = () => {
    setShowPopup(false);
    setScanValue('');
    setScanBuffer('');
    setScanError(null);
  };

  const handleSaveClick = () => {
    if (selectedTrayId && taskId) {
      const reported = ReportIncrementalToolStart(selectedTrayId, Number(taskId));
      reported?.then(() => {
        if (selectedToolModel) {
          GetToolBomToolManagementListService(
            selectedToolModel,
            getToolBOMFactoryIdFromLocalStorage() || '',
            Number(getTenantIdFromLocalStorage())
          );
        }
      });
    }
    closePopup();
  };

  return (
    <>
      <div className="card-title">
        <button
          type="button"
          className={`btn btn-outline-primary ${!canScan ? 'disabled' : ''}`}
          disabled={!canScan}
          onClick={() => setShowPopup(true)}
        >
          <FormattedMessage id="tool_scan" />
        </button>
      </div>

      {showPopup && (
        <Modal
          show={showPopup}
          centered
          size="lg"
          className="tool-scan-modal"
        >
          <Modal.Header>
            <Modal.Title>
              <FormattedMessage id="tool_scan" />
              <button
                onClick={closePopup}
                className="Crossicons"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="23"
                  height="23"
                  fill="currentColor"
                  className="bi bi-x"
                  viewBox="0 0 16 16"
                >
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                </svg>
              </button>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-6">
                <label className="form-label">
                  <FormattedMessage id="tool_management_no" />
                </label>
                <input
                  ref={inputRef}
                  type="text"
                  className={`form-control ${scanError ? 'is-invalid' : ''}`}
                  autoFocus
                  value={scanValue}
                  onChange={(e) => {
                    setScanValue(e.target.value);
                    if (scanError) setScanError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleScanSubmit();
                    }
                  }}
                />
                {scanError && (
                  <div className="invalid-feedback d-block">
                    {scanError.key ? (
                      <FormattedMessage
                        id={scanError.key}
                        values={{ toolNo: scanError.toolNo }}
                      />
                    ) : (
                      scanError.message
                    )}
                  </div>
                )}
              </div>
              <div className="col-6">
                <label className="form-label">
                  <FormattedMessage id="scanned_tools" />
                </label>
                <ul className="list-group">
                  {scannedToolNumbers.length > 0 ? (
                    scannedToolNumbers.map((toolNo) => {
                      const isSaved =
                        isSingleMode && sentScannedToolNumbers.includes(toolNo);
                      return (
                        <li
                          key={toolNo}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          {toolNo}
                          {scannedToolMainUnitNumbers.includes(toolNo) && ' (M)'}
                          <button
                            type="button"
                            className="btn btn-link p-0 text-danger"
                            title={
                              isSaved
                                ? formatMessage('tool_already_saved')
                                : formatMessage('delete')
                            }
                            disabled={isSaved}
                            onClick={() => removeScannedTool(toolNo)}
                          >
                            <XCircleFill size={20} />
                          </button>
                        </li>
                      );
                    })
                  ) : (
                    <li className="list-group-item text-muted text-center">
                      <FormattedMessage id="no_tools_scanned" />
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveClick}
            >
              <FormattedMessage id="set" />
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default observer(ToolScanControl);
